import { useFormContext } from "react-hook-form";
import {
  Field,
  FontWeight,
  FormFieldStatus,
  Hint,
  Input,
  Label,
  TextAlign,
  TypeScale,
  useTheme,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import {
  EmailVerificationFormData,
  VerificationCodeAllowedChar,
} from "../../consts";

import { getInputErrorStatus } from "src/utils/inputValidation";

interface VerificationCodeFieldProps {
  verificationCode?: string;
  setVerificationCode(code: string): void;
}

export const VerificationCodeField: React.FC<VerificationCodeFieldProps> = ({
  verificationCode,
  setVerificationCode,
}) => {
  const { theme } = useTheme();
  const { i18n } = useLingui();

  const {
    register,
    formState: { errors },
  } = useFormContext<EmailVerificationFormData>();

  const handleAllowNumbers = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.key;
    if (!VerificationCodeAllowedChar.includes(key)) {
      event.preventDefault();
    }
  };

  const handlePasteEvent = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const inputString = event.clipboardData.getData("text");
    const filteredValue = inputString.replace(/\D/g, "").substring(0, 6);
    setVerificationCode(filteredValue);
  };

  return (
    <Field>
      <Label htmlFor="verificationCode">
        <Trans id="optimised-signup.email-verification.enter-code">
          Enter code
        </Trans>
      </Label>
      <Input
        type="text"
        id="verificationCode"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        autoComplete="one-time-code"
        value={verificationCode}
        status={getInputErrorStatus(!!errors.verificationCode?.message)}
        onKeyDown={handleAllowNumbers}
        onPaste={handlePasteEvent}
        {...register("verificationCode", {
          required: i18n._({
            id: "optimised-signup.email-verification.code-required",
            message: "Verification code is required",
          }),
          onChange: (event) => {
            const { value } = event.currentTarget;
            const filteredValue = value.toString().substring(0, 6);

            setVerificationCode(filteredValue);
          },
        })}
        css={{
          ...theme.tokens.fontSizes[TypeScale.Size_06],
          fontWeight: FontWeight.Normal,
          textAlign: TextAlign.Center,
          fontFamily: "DM Mono",
          letterSpacing: theme.spacing(0.5),
          padding: theme.spacing(0.5),
        }}
      />
      {errors.verificationCode?.message && (
        <Hint id="verificationCodeHelp" status={FormFieldStatus.Danger}>
          {errors.verificationCode.message}
        </Hint>
      )}
    </Field>
  );
};
